import { useLocation, useNavigate } from 'react-router-dom';
import type { EChartsOption } from 'echarts';
import { Avatar, Breadcrumb, Button, EChart, PageHeader } from '@jordan/ui';
import { AppChrome } from '../../components/AppChrome';
import { useMe } from '../../auth/MeProvider';
import { billingTimeSidebar } from './billingTimeSidebar';

// Placeholder dashboard data (no time-tracking backend yet) — mirrors the reference overview.
const WEEK = [
  { day: 'Mon', date: '06/08', seconds: 0 },
  { day: 'Tue', date: '06/09', seconds: 0 },
  { day: 'Wed', date: '06/10', seconds: 2700 },
  { day: 'Thu', date: '06/11', seconds: 0 },
  { day: 'Fri', date: '06/12', seconds: 0 },
  { day: 'Sat', date: '06/13', seconds: 0 },
  { day: 'Sun', date: '06/14', seconds: 140 },
];
const WEEK_TOTAL = '0:47:20';

const hms = (s: number) => {
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
};

function weekChart(): EChartsOption {
  const seconds = WEEK.map((d) => d.seconds);
  return {
    textStyle: { fontFamily: 'var(--body-font-family)', color: 'var(--body-color)' },
    grid: { left: 8, right: 16, top: 24, bottom: 24, containLabel: true },
    tooltip: {
      trigger: 'axis',
      formatter: (p: any) => {
        const i = p[0].dataIndex as number;
        return `<div class="fw-semibold">${WEEK[i].day} ${WEEK[i].date}</div>${hms(WEEK[i].seconds)}`;
      },
    },
    xAxis: {
      type: 'category',
      data: WEEK.map((d) => `${d.day} ${d.date}`),
      axisLabel: { color: 'rgba(var(--body-color-rgb), .65)' },
      axisTick: { show: false },
      axisLine: { lineStyle: { color: 'var(--gray-300)' } },
    },
    yAxis: {
      type: 'value',
      min: 0,
      max: 4500,
      interval: 900, // 15-minute gridlines
      axisLabel: {
        color: 'rgba(var(--body-color-rgb), .55)',
        formatter: (v: number) => {
          const h = Math.floor(v / 3600);
          const m = Math.floor((v % 3600) / 60);
          return m === 0 ? `${h}h` : `${h}h ${m}`;
        },
      },
      splitLine: { lineStyle: { color: 'var(--gray-200)', type: 'dashed' } },
    },
    series: [
      {
        name: 'Non-billable',
        type: 'bar',
        barWidth: 30,
        itemStyle: { color: 'var(--bs-primary)', borderRadius: [3, 3, 0, 0] },
        data: seconds,
        label: {
          show: true,
          position: 'top',
          color: 'rgba(var(--body-color-rgb), .65)',
          fontSize: 11,
          formatter: (p: any) => (p.value > 0 ? hms(WEEK[p.dataIndex].seconds) : ''),
        },
      },
    ],
  };
}

/** A simple donut/ring gauge for a single percentage. */
function Ring({ percent, color, label, size = 150 }: { percent: number; color: string; label: string; size?: number }) {
  const stroke = 16;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const dash = (Math.max(0, Math.min(100, percent)) / 100) * c;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--gray-200)" strokeWidth={stroke} />
      {dash > 0 && (
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeDasharray={`${dash} ${c - dash}`}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      )}
      <text x="50%" y="50%" textAnchor="middle" dominantBaseline="central" className="fw-bold" fontSize={size * 0.2} fill="var(--body-color)">
        {label}
      </text>
    </svg>
  );
}

const FAQ = [
  'How should I set up my workspace?',
  'How do roles and permissions work?',
  'How do I set billable rates?',
  'How can I ensure accurate time tracking?',
  'How do I create and use reports?',
];

export function BillingTimeOverviewPage() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const me = useMe();
  const myName = me?.displayName ?? 'You';
  const myInitials = (me?.displayName ?? 'Y').slice(0, 2);

  return (
    <AppChrome secondarySidebar={billingTimeSidebar(navigate, pathname)}>
      <PageHeader
        title={
          <>
            Billing &amp; Time - <span className="fw-normal">Overview</span>
          </>
        }
        breadcrumb={
          <Breadcrumb
            items={[
              { icon: 'ph-house', href: '/' },
              { label: 'Consulting' },
              { label: 'Billing & Time' },
              { label: 'Overview' },
            ]}
          />
        }
      />

      <div className="content">
        <div className="row g-3">
          {/* Left column */}
          <div className="col-xl-8">
            <div className="card border-0 shadow-sm mb-3">
              <div className="card-header bg-transparent d-flex align-items-center">
                <h5 className="mb-0 me-auto">This week summary</h5>
                <a href="/consulting/billing-time/reports" className="text-primary fw-semibold fs-sm text-decoration-none" onClick={(e) => { e.preventDefault(); navigate('/consulting/billing-time/reports'); }}>
                  VIEW REPORTS
                </a>
              </div>
              <div className="card-body">
                <EChart option={weekChart()} height={300} />
                <div className="d-flex justify-content-center align-items-center mt-2 fs-sm text-muted">
                  <span className="d-inline-block rounded me-2" style={{ width: 12, height: 12, background: 'var(--bs-primary)' }} />
                  Non-billable
                </div>
              </div>
            </div>

            <div className="card border-0 shadow-sm">
              <div className="card-header bg-transparent d-flex align-items-center">
                <h5 className="mb-0 me-auto">Team activity</h5>
                <a href="#" className="text-primary fw-semibold fs-sm text-decoration-none" onClick={(e) => e.preventDefault()}>
                  VIEW TEAM ACTIVITY
                </a>
              </div>
              <div className="card-body">
                <div className="row align-items-center g-4">
                  <div className="col-md-4 text-center">
                    <Ring percent={100} color="var(--bs-primary)" label="100%" />
                  </div>
                  <div className="col-md-8">
                    <div className="row">
                      <div className="col-6">
                        <div className="text-uppercase fs-sm text-muted mb-2">Tracking</div>
                        <div className="d-flex align-items-center">
                          <Avatar size={32} initials={myInitials} status="success" />
                          <div className="ms-2">
                            <div className="fw-semibold">{myName}</div>
                            <div className="text-muted fs-sm">{WEEK_TOTAL}</div>
                          </div>
                        </div>
                      </div>
                      <div className="col-6">
                        <div className="text-uppercase fs-sm text-muted mb-2">Not tracking</div>
                        <div className="text-muted fs-sm">Everyone’s tracking.</div>
                      </div>
                    </div>
                    <div className="mt-4">
                      <span className="fw-bold">1</span> out of <span className="fw-bold">1</span> member tracking
                      <div className="text-muted fs-sm">Bring your team to see the full picture</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right column */}
          <div className="col-xl-4">
            <div className="card border-0 shadow-sm mb-3">
              <div className="card-body">
                <div className="d-flex align-items-center mb-3">
                  <h2 className="fw-bold mb-0 me-auto">1</h2>
                  <div className="d-flex">
                    {['A', 'B', 'C'].map((c, i) => (
                      <span key={c} style={{ marginLeft: i ? -8 : 0 }}>
                        <Avatar size={24} initials={c} />
                      </span>
                    ))}
                  </div>
                </div>
                <h6 className="fw-semibold">Members in your organization</h6>
                <p className="text-muted fs-sm">
                  Your insights are incomplete if you’re tracking alone. Invite your team to see the full picture.
                </p>
                <Button variant="primary" icon="ph-user-plus">
                  Add teammates
                </Button>
                <div className="fs-sm text-muted mt-3">
                  Bringing on a large team?{' '}
                  <a href="#" className="text-primary" onClick={(e) => e.preventDefault()}>
                    book a demo
                  </a>
                </div>
              </div>
            </div>

            <div className="card border-0 shadow-sm mb-3">
              <div className="card-header bg-transparent d-flex align-items-center">
                <h6 className="mb-0 me-auto fw-semibold">Top projects this week</h6>
                <a href="/consulting/billing-time/reports" className="text-primary fw-semibold fs-sm text-decoration-none" onClick={(e) => { e.preventDefault(); navigate('/consulting/billing-time/reports'); }}>
                  VIEW REPORTS
                </a>
              </div>
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <span className="d-inline-block rounded-circle me-2" style={{ width: 10, height: 10, background: 'var(--gray-500)' }} />
                  <span className="me-auto">Without project</span>
                  <span className="fw-semibold">{WEEK_TOTAL}</span>
                </div>
              </div>
            </div>

            <div className="card border-0 shadow-sm mb-3">
              <div className="card-header bg-transparent">
                <h6 className="mb-0 fw-semibold">Time tracked to projects</h6>
              </div>
              <div className="card-body text-center">
                <Ring percent={0} color="var(--bs-primary)" label="0%" />
                <div className="mt-3">
                  <a href="/consulting/billing-time/projects" className="text-primary fw-semibold fs-sm text-decoration-none" onClick={(e) => { e.preventDefault(); navigate('/consulting/billing-time/projects'); }}>
                    ADD MEMBER TO PROJECT
                  </a>
                </div>
              </div>
            </div>

            <div className="card border-0 shadow-sm">
              <div className="card-header bg-transparent d-flex align-items-center">
                <div className="me-auto">
                  <h6 className="mb-0 fw-semibold">FAQ</h6>
                  <span className="text-muted fs-sm">The stuff most admins ask us</span>
                </div>
                <a href="#" className="text-primary fw-semibold fs-sm text-decoration-none" onClick={(e) => e.preventDefault()}>
                  VIEW MORE
                </a>
              </div>
              <div className="card-body">
                {FAQ.map((q) => (
                  <a
                    key={q}
                    href="#"
                    className="d-block py-1 text-body text-decoration-none"
                    onClick={(e) => e.preventDefault()}
                  >
                    {q}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </AppChrome>
  );
}
